import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { advancedNumber } from '../../utils/advancedStatistics';
import { findSimilarPlayers, isGoalkeeper, playerPosition } from '../../utils/playerDiscovery';
import PlayerRoleBadge from '../playerStatistics/PlayerRoleBadge';
import { playerKey, playerName } from '../../utils/playerRanking';
import './playerDiscovery.css';

export default function PlayerSimilarity({ records, candidates, params, onChange, profileUrl, profileState, onCompare }) {
  const { t, i18n } = useTranslation('directories');
  const theme = useTheme();
  const [limit, setLimit] = useState(10);
  const [expanded, setExpanded] = useState(null);
  const targetId = params.get('similar') || '';
  const target = records.find(player => playerKey(player) === targetId);
  const mode = params.get('similarMode') === 'advanced' ? 'advanced' : 'basic';
  const positionMode = params.get('similarPosition') === 'exact' ? 'exact' : 'family';
  const minimum = key => params.has(key) ? advancedNumber(params.get(key)) : key === 'similarMinutes' ? 450 : 3;
  const minMinutes = minimum('similarMinutes'), minMatches = minimum('similarMatches');
  const invalid = minMinutes === null || minMinutes < 0 || minMatches === null || minMatches < 0;
  const search = useMemo(() => !target || invalid ? null :
    findSimilarPlayers(target, candidates, { mode, positionMode, minMinutes, minMatches }),
  [target, candidates, mode, positionMode, minMinutes, minMatches, invalid]);
  const format = value => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
  const change = values => { setLimit(10); onChange(values); };
  return <section className="dr-panel pd-similarity" aria-labelledby="player-similarity-title">
    <header className="dr-panel-header"><div>
      <p className="pd-eyebrow">{t('discovery.similarEyebrow')}</p>
      <h2 id="player-similarity-title">{t('discovery.similarTitle')}</h2>
      <p className="dr-muted">{t('discovery.similarIntro')}</p>
    </div>{targetId && <Button onClick={() => change({ similar: null })}>{t('discovery.clearReference')}</Button>}</header>
    <div className="pd-fields">
      <label className="pd-reference">{t('discovery.reference')}<select value={target ? targetId : ''}
        onChange={event => change({ similar: event.target.value })}>
        <option value="">{t('choosePlayer')}</option>
        {records.map(player => <option key={playerKey(player)} value={playerKey(player)}>
          {playerName(player)} · {player.nombreEquipo || player.equipo} · {playerPosition(player) || '—'}
        </option>)}
      </select></label>
      <label>{t('discovery.dataMode')}<select value={mode} onChange={event => change({ similarMode: event.target.value })}>
        <option value="basic">{t('discovery.basic')}</option><option value="advanced">{t('discovery.advanced')}</option>
      </select></label>
      <label>{t('discovery.positionMode')}<select value={positionMode}
        onChange={event => change({ similarPosition: event.target.value })}>
        <option value="family">{t('discovery.sameFamily')}</option><option value="exact">{t('discovery.samePosition')}</option>
      </select></label>
      {['similarMinutes', 'similarMatches'].map(key => <label key={key}>{t(`discovery.${key}`)}
        <input type="number" min="0" step="1" value={params.has(key) ? params.get(key) : key === 'similarMinutes' ? 450 : 3}
          onChange={event => change({ [key]: event.target.value === '' ? '0' : event.target.value })} />
      </label>)}
    </div>
    <p className="pd-footnote">{t(mode === 'basic' ? 'discovery.basicLimit' : 'discovery.advancedLimit')}</p>
    {targetId && !target && <Alert severity="info">{t('discovery.referenceMissing')}</Alert>}
    {invalid && <Alert severity="warning">{t('discovery.invalidFilters')}</Alert>}
    {target && search?.reason && <Alert severity="info">{t(`discovery.reasons.${search.reason}`)}</Alert>}
    {target && search && !search.reason && <>
      <p className="pd-method">{t('discovery.method', { count: search.metrics.length, players: search.cohortSize })}</p>
      <p className="dr-muted">{search.metrics.map(id => t(`discovery.metrics.${id}`)).join(' · ')}</p>
      <div className="dr-table-scroll" role="region" tabIndex={0} aria-label={t('discovery.similarResults')}>
        <table className="dr-table pd-similar-table" aria-label={t('discovery.similarResults')}>
          <thead><tr><th scope="col">{t('searchPlayer')}</th><th scope="col">{t('discovery.team')}</th>
            <th scope="col">{t('discovery.position')}</th><th scope="col">{t('discovery.affinity')}</th>
            <th scope="col">{t('discovery.actions')}</th></tr></thead>
          <tbody>{search.results.slice(0, limit).map(({ player, score, differences }) => <React.Fragment key={playerKey(player)}>
            <tr className={`pd-result${isGoalkeeper(player) ? ' player-goalkeeper' : ''}`} data-theme={theme.palette.mode}><th scope="row">
              <Link className="dr-name" to={profileUrl(player)} state={profileState(player)}>{playerName(player)}</Link>
              <PlayerRoleBadge player={player} />
            </th><td>{player.nombreEquipo || player.equipo}</td><td>{playerPosition(player)}</td>
              <td className="pd-affinity">{format(score)}</td><td><div className="pd-row-actions">
                <Button variant="outlined" onClick={() => onCompare(target, player)}>{t('comparePlayers')}</Button>
                <Button aria-expanded={expanded === playerKey(player)}
                  aria-controls={`similar-details-${playerKey(player)}`}
                  onClick={() => setExpanded(current => current === playerKey(player) ? null : playerKey(player))}>
                  {t('discovery.whySimilar')}
                </Button>
              </div></td></tr>
            {expanded === playerKey(player) && <tr id={`similar-details-${playerKey(player)}`}>
              <td colSpan={5} className="pd-similar-details">
            <p>{t('discovery.closest', { metrics: differences.slice(0, 2).map(item => t(`discovery.metrics.${item.id}`)).join(', ') })}</p>
            <p>{t('discovery.largestDifference', { metric: t(`discovery.metrics.${differences.at(-1).id}`) })}</p>
            <div className="dr-table-scroll" role="region" tabIndex={0} aria-label={t('discovery.similarityComparison', { name: playerName(player) })}>
              <table className="dr-table"><thead><tr><th>{t('discovery.metric')}</th>
                <th>{playerName(target)}</th><th>{playerName(player)}</th></tr></thead>
                <tbody>{differences.map(item => <tr key={item.id}><th scope="row">{t(`discovery.metrics.${item.id}`)}</th>
                  <td>{format(item.target)}</td><td>{format(item.candidate)}</td></tr>)}</tbody>
              </table>
            </div>
              </td></tr>}
          </React.Fragment>)}</tbody>
        </table>
      </div>
      {search.results.length > limit && <Button onClick={() => setLimit(value => value + 10)}>{t('discovery.moreSimilar')}</Button>}
    </>}
    <p className="pd-footnote">{t('discovery.similarityDisclaimer')}</p>
  </section>;
}
